import { useState, useCallback } from 'react'
import { FileText, Globe, Youtube, X, Plus, Loader2 } from 'lucide-react'
import { FileDropzone } from '../../ui/file-dropzone'
import { apiRequestForm } from '../../../api/config'
import type { UseCase } from './use-case-step'

export interface MaterialItem {
  url: string
  name: string
  type: 'file' | 'link' | 'youtube'
  sizeBytes?: number
}

interface MaterialsStepProps {
  materials: MaterialItem[]
  useCase: UseCase | null
  onChange: (materials: MaterialItem[]) => void
}

const SUPPORTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/markdown',
]

const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

function classifyUrl(url: string): 'youtube' | 'link' {
  if (/youtu(\.be|be\.com)/i.test(url)) return 'youtube'
  return 'link'
}

function isValidUrl(str: string): boolean {
  try {
    const url = new URL(str)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

const HEADINGS: Record<UseCase, { heading: string; subheading: string }> = {
  'busy-professional': {
    heading: 'Got any material to include?',
    subheading: 'Upload documents, link articles, or paste YouTube videos you want covered.',
  },
  'student-learner': {
    heading: 'Upload your study materials',
    subheading:
      "Add lecture slides, PDFs, textbook chapters, or YouTube links. We'll weave them in.",
  },
  'curious-explorer': {
    heading: 'Add reference material',
    subheading: "Link articles, videos, or upload documents you'd like explored.",
  },
}

const DEFAULT_HEADING = {
  heading: 'Add source materials',
  subheading:
    "Upload files or paste URLs for content you want included.",
}

export function MaterialsStep({ materials, useCase, onChange }: MaterialsStepProps) {
  const [linkInput, setLinkInput] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const config = useCase ? HEADINGS[useCase] : DEFAULT_HEADING

  const addLink = useCallback(() => {
    const trimmed = linkInput.trim()
    if (!trimmed) return

    if (!isValidUrl(trimmed)) {
      setError('Please enter a valid URL (https://...)')
      return
    }

    // Dedupe
    if (materials.some((m) => m.url === trimmed)) {
      setError('Already added')
      return
    }

    setError(null)
    const type = classifyUrl(trimmed)
    const hostname = new URL(trimmed).hostname.replace('www.', '')
    onChange([...materials, { url: trimmed, name: hostname, type }])
    setLinkInput('')
  }, [linkInput, materials, onChange])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addLink()
    }
  }

  const handleFileDrop = useCallback(
    async (files: File[]) => {
      setError(null)
      setUploading(true)

      try {
        const results: MaterialItem[] = []

        for (const file of files) {
          // Check for duplicates by name
          if (materials.some((m) => m.name === file.name && m.type === 'file')) continue

          const formData = new FormData()
          formData.append('file', file)

          const result = await apiRequestForm<{ url: string; originalName: string; size: number }>(
            '/api/upload/series-material',
            formData,
          )

          results.push({
            url: result.url,
            name: result.originalName,
            type: 'file',
            sizeBytes: result.size,
          })
        }

        onChange([...materials, ...results])
      } catch (err) {
        console.error('Upload failed:', err)
        setError('Failed to upload one or more files. Please try again.')
      } finally {
        setUploading(false)
      }
    },
    [materials, onChange],
  )

  const removeMaterial = (index: number) => {
    onChange(materials.filter((_, i) => i !== index))
  }

  const iconForType = (type: string) => {
    switch (type) {
      case 'youtube':
        return <Youtube className="h-4 w-4 text-foreground/60" />
      case 'file':
        return <FileText className="h-4 w-4 text-foreground/60" />
      default:
        return <Globe className="h-4 w-4 text-foreground/60" />
    }
  }

  const formatSize = (bytes?: number) => {
    if (!bytes) return null
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div>
      <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-foreground">{config.heading}</h2>
      <p className="mb-8 text-[15px] font-medium text-foreground/60">{config.subheading}</p>

      {/* File upload */}
      <div className="mb-8">
        <label className="mb-3 block text-[15px] font-bold text-foreground">Upload files</label>
        <FileDropzone
          accept={SUPPORTED_FILE_TYPES}
          maxSize={MAX_FILE_SIZE}
          onDrop={(files) => void handleFileDrop(files)}
          multiple
          placeholder={uploading ? 'Uploading...' : 'Drop files here or click to browse'}
          maxSizeLabel="PDF, DOCX, PPTX, TXT, MD - up to 50 MB each"
        />
        {uploading && (
          <div className="mt-3 flex items-center gap-2 text-[13px] font-bold text-brand-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Uploading...
          </div>
        )}
      </div>

      {/* Link input */}
      <div className="mb-8 border-t border-black/5 pt-8">
        <label className="mb-3 block text-[15px] font-bold text-foreground">Add links</label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="url"
            value={linkInput}
            onChange={(e) => {
              setLinkInput(e.target.value)
              setError(null)
            }}
            onKeyDown={handleKeyDown}
            placeholder="https://example.com/article or YouTube URL"
            className="flex-1 rounded-2xl border border-black/10 bg-white px-5 py-3 text-[15px] text-foreground shadow-sm placeholder:text-foreground/30 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
          />
          <button
            onClick={addLink}
            disabled={!linkInput.trim()}
            className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl bg-brand-600 px-6 py-3 text-[15px] font-bold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-secondary-300 disabled:opacity-50"
          >
            <Plus className="h-5 w-5" />
            Add
          </button>
        </div>
        {error && <p className="mt-2 text-[13px] font-bold text-danger-600">{error}</p>}
      </div>

      {/* Material list */}
      {materials.length > 0 && (
        <div className="mb-8 mt-8 border-t border-black/5 pt-8">
          <label className="mb-4 block text-[15px] font-bold text-foreground">
            {materials.length} material{materials.length !== 1 ? 's' : ''} added
          </label>
          <div className="space-y-3">
            {materials.map((material, index) => (
              <div
                key={material.url}
                className="flex items-center gap-4 rounded-3xl border border-black/5 bg-white px-5 py-4 shadow-sm transition-colors"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  {iconForType(material.type)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold text-foreground">{material.name}</p>
                  <p className="truncate text-[13px] font-medium text-foreground/50">
                    {material.type === 'file' && formatSize(material.sizeBytes)
                      ? `${formatSize(material.sizeBytes)} · `
                      : ''}
                    {material.url}
                  </p>
                </div>
                <button
                  onClick={() => removeMaterial(index)}
                  className="cursor-pointer rounded-xl p-2 text-foreground/30 transition-colors hover:bg-danger-50 hover:text-danger-600"
                  title="Remove material"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {materials.length === 0 && (
        <p className="mt-8 text-center text-[13px] text-foreground/40">
          This step is optional - you can always add materials later from the series settings.
        </p>
      )}
    </div>
  )
}
